/**
 * Checagens de rede do passo de e-mail (consulta paga duplicada +
 * `/registration/start`), rodadas depois de o paciente já ter ido para o
 * próximo passo — esperar as duas em série deixava a tela parada por segundos.
 *
 * O `/registration/start` grava o documento com `set()` sem merge: um
 * `/registration/step` que chegue antes dele tem o CPF ou o telefone apagado.
 * Por isso os passos seguintes esperam `awaitPendingRegistrationStart`.
 */
import {
  tryRecoverPaidPatientId,
  adoptExistingPaidPatientId,
  adoptFromBackend409Payload,
  resolvePaidRecoveryRoute,
} from './paidPartialRecovery';
import { ensureSignupSession } from './ensureSignupSession';
import { logUxEvent } from '../telemetry/uxLogger';

export const REGISTRATION_START_TIMEOUT_MS = 15000;
export const CONSULTATION_STATUS_TIMEOUT_MS = 4000;

export const ACCESS_BLOCKED_STORAGE_KEY = 'signupAccessBlockedMessage';

const SIGNUP_ROUTES_AFTER_EMAIL = new Set(['/email', '/email/confirm', '/cpf', '/telefone', '/termos']);

/**
 * Redirecionar por consulta paga duplicada ou bloqueio só vale nos passos de
 * cadastro; depois deles o aceite dos Termos e o /pagamento refazem as duas
 * checagens, e tirar o paciente da triagem no meio seria pior.
 */
export function isStillInSignupSteps(pathname = typeof window !== 'undefined' ? window.location.pathname : '') {
  return SIGNUP_ROUTES_AFTER_EMAIL.has(String(pathname || '').replace(/\/+$/, '') || '/');
}

export function peekPendingAccessBlockedMessage() {
  try { return sessionStorage.getItem(ACCESS_BLOCKED_STORAGE_KEY) || null; } catch { return null; }
}

export function clearPendingAccessBlockedMessage() {
  try { sessionStorage.removeItem(ACCESS_BLOCKED_STORAGE_KEY); } catch {}
}

export function takePendingAccessBlockedMessage() {
  const pending = peekPendingAccessBlockedMessage();
  if (pending) clearPendingAccessBlockedMessage();
  return pending;
}

export function storePendingAccessBlockedMessage(message) {
  try { sessionStorage.setItem(ACCESS_BLOCKED_STORAGE_KEY, message || ''); } catch {}
  try {
    window.dispatchEvent(new CustomEvent('cm:signup-access-blocked'));
  } catch {}
}

/**
 * O "Continuar como" confirma e-mail, CPF e telefone ao mesmo tempo. A
 * resposta de bloqueio ou de consulta já paga chega no meio disso, e a
 * navegação que ainda estava em andamento (o CPF espera o cupom) cobria o
 * redirecionamento. Quem começou antes da resposta compara a geração e não
 * navega. Um toque novo do paciente, depois, segue normalmente.
 */
let signupNavGeneration = 0;

export function currentSignupNavGeneration() {
  return signupNavGeneration;
}

export function invalidateSignupNavigation() {
  signupNavGeneration += 1;
}

let pendingStart = null;

export function hasPendingRegistrationStart() {
  return pendingStart !== null;
}

export function awaitPendingRegistrationStart(maxMs = REGISTRATION_START_TIMEOUT_MS) {
  if (!pendingStart) return Promise.resolve();
  return Promise.race([
    pendingStart,
    new Promise((resolve) => setTimeout(resolve, maxMs)),
  ]);
}

async function postRegistrationStart(baseUrl, body) {
  const ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timer = ctrl ? setTimeout(() => ctrl.abort(), REGISTRATION_START_TIMEOUT_MS) : null;
  try {
    return await fetch(`${baseUrl}/registration/start`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      ...(ctrl ? { signal: ctrl.signal } : {}),
    });
  } finally {
    if (timer) clearTimeout(timer);
  }
}

/**
 * O `ensureSignupSession` reaproveita o patientId que o aparelho já tem, e o
 * canal do chat é `{patientId}-{médico}`. Se a consulta desse id já foi
 * encerrada ou venceu, o chat expulsa para a Home logo depois da triagem
 * (11/10/2026: id de junho com consulta finalizada em 10/09, sem pagamento —
 * o 409 `session_already_paid` só cobre cadastro pago). Falha de rede = segue.
 */
async function isPatientConsultationClosed(baseUrl, patientId) {
  const ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timer = ctrl ? setTimeout(() => ctrl.abort(), CONSULTATION_STATUS_TIMEOUT_MS) : null;
  try {
    const res = await fetch(
      `${baseUrl}/prontuario/consultation-status?patientId=${encodeURIComponent(patientId)}`,
      { method: 'GET', ...(ctrl ? { signal: ctrl.signal } : {}) },
    );
    if (!res || !res.ok) return false;
    const data = await res.json().catch(() => null);
    return !!(data && data.success && data.exists && (data.finalized || data.expired));
  } catch {
    return false;
  } finally {
    if (timer) clearTimeout(timer);
  }
}

async function rotatePatientId() {
  const fresh = await ensureSignupSession({ forceNew: true }).catch(() => null);
  return (fresh && fresh.patientId) || '';
}

/**
 * @param {object} args
 * @param {string} args.baseUrl
 * @param {string} args.email - já normalizado (trim + lowercase)
 * @param {string} args.patientId
 * @param {string} args.fullName
 * @param {(route: string) => void} args.onPaidRecovered - consulta paga ativa encontrada
 * @param {(message: string|null, code: string|null) => void} args.onBlocked - 403 de bloqueio
 * @param {string} [args.source] - prefixo da origem gravada na adoção do patientId pago
 * @returns {Promise<'recovered'|'blocked'|'started'|'skipped'|'failed'>}
 */
export function runEmailStepRegistration({
  baseUrl, email, patientId, fullName, onPaidRecovered, onBlocked, source = 'email_form',
}) {
  const run = async () => {
    try {
      const recovered = await tryRecoverPaidPatientId(baseUrl, { email });
      if (recovered) {
        adoptExistingPaidPatientId({
          ...recovered,
          source: `${source}_lookup`,
          newlyCreatedPatientId: patientId || null,
        });
        onPaidRecovered(resolvePaidRecoveryRoute(recovered));
        return 'recovered';
      }
    } catch (_) { /* fail-open: segue para o start */ }

    if (!patientId || !email) return 'skipped';

    let startPatientId = patientId;
    if (await isPatientConsultationClosed(baseUrl, patientId)) {
      const newId = await rotatePatientId();
      if (newId) {
        startPatientId = newId;
        try { logUxEvent('signup_patient_id_rotated', { reason: 'consultation_closed', source }); } catch {}
      }
    }

    try {
      const res = await postRegistrationStart(baseUrl, { patientId: startPatientId, fullName, email });
      if (res.status === 409) {
        const payload = await res.json().catch(() => ({}));
        if (payload && payload.error === 'paid_partial_exists') {
          adoptFromBackend409Payload(payload, `${source}_409`);
          onPaidRecovered(resolvePaidRecoveryRoute({
            status: payload.existingStatus,
            activeChannelId: payload.activeChannelId,
            channelOpen: !!payload.activeChannelId,
          }));
          return 'recovered';
        }
        if (payload && payload.error === 'session_already_paid') {
          const newId = await rotatePatientId();
          if (newId) {
            await postRegistrationStart(baseUrl, { patientId: newId, fullName, email }).catch(() => null);
          }
          return 'started';
        }
      }
      if (res.status === 403) {
        const payload = await res.json().catch(() => ({}));
        if (payload && payload.blocked === true) {
          onBlocked(payload.message || null, payload.code || null);
          return 'blocked';
        }
      }
      return 'started';
    } catch (_) {
      return 'failed';
    }
  };

  const p = run();
  const tracked = p.catch(() => 'failed').finally(() => {
    if (pendingStart === tracked) pendingStart = null;
  });
  pendingStart = tracked;
  return tracked;
}
